import { useState } from "react";
import { ChevronDown, Clock, FileText, Landmark, Flag, ScrollText, IndianRupee, Link2, ListOrdered } from "lucide-react";
import { StatusChip } from "./ui-bits";

function ApprovalDetails({ a }) {
  return (
    <div className="px-4 pb-4 pt-1 border-t border-[#0B1F3A]/10 text-sm space-y-3 fade-up">
      {a.conditional_reason && (
        <p className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-amber-800 text-xs">{a.conditional_reason}</p>
      )}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-1"><Landmark size={12} /> Issuing authority</p>
        <p className="text-[#0B1F3A] mt-0.5">{a.authority}</p>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-1"><ScrollText size={12} /> Statutory basis</p>
        <p className="text-[#0B1F3A] mt-0.5">{a.statutory_basis}</p>
      </div>
      {a.process_note && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#475569]">State/path-specific process{a.variant_name ? ` — ${a.variant_name}` : ""}</p>
          <p className="text-[#0B1F3A] mt-0.5">{a.process_note}</p>
        </div>
      )}
      {a.sub_steps?.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-1"><ListOrdered size={12} /> Sequence</p>
          <ul className="mt-1 space-y-1">
            {a.sub_steps.map((s) => <li key={s} className="text-[#0B1F3A] pl-2 border-l-2 border-[#059669]/50">{s}</li>)}
          </ul>
        </div>
      )}
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-1"><FileText size={12} /> Documents required</p>
        <ul className="mt-1 list-disc list-inside space-y-0.5 text-[#0B1F3A]">
          {a.documents?.map((d) => <li key={d}>{d}</li>)}
        </ul>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-1"><IndianRupee size={12} /> Fees</p>
          <p className="text-[#0B1F3A] mt-0.5">{a.fees_note || "As per authority schedule"}</p>
        </div>
        {a.dependency_names?.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#475569] flex items-center gap-1"><Link2 size={12} /> Apply after</p>
            <p className="text-[#0B1F3A] mt-0.5">{a.dependency_names.join(" · ")}</p>
          </div>
        )}
      </div>
      {a.expert_note && (
        <div className="bg-[#F5F8FF] border border-[#059669]/40 rounded-md px-3 py-2">
          <p className="text-xs font-semibold text-[#059669] uppercase tracking-wider">Solarian expert note</p>
          <p className="text-[#0B1F3A] text-xs mt-1">{a.expert_note}</p>
        </div>
      )}
    </div>
  );
}

export default function ApprovalCard({ approval, index, adminView }) {
  const [open, setOpen] = useState(false);
  const a = approval;
  return (
    <div className={`bg-white rounded-lg border transition-shadow hover:shadow-md ${a.critical ? "border-[#059669] border-l-4" : "border-[#0B1F3A]/15"}`} data-testid={`approval-card-${a.key}`}>
      <div role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setOpen(!open); }} className="w-full text-left px-4 py-3 flex items-start gap-3 cursor-pointer" onClick={() => setOpen(!open)} data-testid={`approval-card-toggle-${a.key}`}>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {index != null && <span className="text-xs font-semibold text-[#059669]">{index}.</span>}
            <h4 className="font-serif font-semibold text-[#0B1F3A] text-sm sm:text-base">{a.name}</h4>
            <StatusChip status={adminView && a.kb_status === "ai_draft" ? "ai_draft" : a.status} />
            {a.critical && <span className="text-[10px] uppercase tracking-wider font-bold text-[#059669]">Critical path</span>}
          </div>
          <p className="text-sm text-[#475569] mt-1">{a.plain_summary}</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-[#475569]">
            <span className="inline-flex items-center gap-1"><Landmark size={12} /> {a.authority?.split("—")[0].split("(")[0].trim().slice(0, 60)}</span>
            <span className="inline-flex items-center gap-1"><Clock size={12} /> ~{a.timeline_days} days (indicative)</span>
            <span className="inline-flex items-center gap-1"><Flag size={12} /> Complete before: {a.plant_milestone}</span>
          </div>
        </div>
        <ChevronDown size={18} className={`shrink-0 mt-1 text-[#475569] transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && <ApprovalDetails a={a} />}
    </div>
  );
}
